import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LogOut, Search, Filter, Users, Plane, CheckCircle, Clock, XCircle, Trash2,
  Mail, Phone, MapPin, Calendar, TrendingUp, RefreshCw, ChevronDown,
  FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Eye, BarChart3, MessageCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AdminReviewsPanel from '../components/AdminReviewsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-700', icon: Mail },
  confirmed: { label: 'Confirmé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const tripTypeLabels = {
  oneWay: 'Aller simple',
  roundTrip: 'Aller-retour',
  multiCity: 'Multi-destination'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [activeTab, setActiveTab] = useState('bookings');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('page_size', pageSize);

      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings?${params.toString()}`, authHeaders()),
        axios.get(`${API}/admin/stats`, authHeaders())
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setTotalPages(bookingsRes.data.total_pages || 1);
      setTotalBookings(bookingsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expirée');
        logout();
        navigate('/admin/login');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, pageSize]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}/status`, { status: newStatus }, authHeaders());
      toast.success('Statut mis à jour');
      fetchData();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Supprimer cette réservation définitivement ?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeaders());
      toast.success('Réservation supprimée');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const exportData = async (format) => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      const url = `${API}/admin/bookings/export/${format}?${params.toString()}`;
      const res = await axios.get(url, { ...authHeaders(), responseType: 'blob' });
      const blob = new Blob([res.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `IZI_Travels_Reservations_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (err) {
      toast.error(`Erreur export ${format}`);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_5e258ebd-49b8-4be0-854e-388039fa29ef/artifacts/fd4luh7q_IZICOLOR.png"
              alt="IZI Travels"
              className="h-10 w-auto"
            />
            <div className="hidden md:block">
              <h1 className="text-xl font-bold text-gray-900">Tableau de bord</h1>
              <p className="text-xs text-gray-500">Gestion des réservations</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              title="Actualiser"
              data-testid="refresh-btn"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-5 py-3 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'bookings'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid="tab-bookings"
          >
            <Plane size={18} /> Réservations
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-5 py-3 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'reviews'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid="tab-reviews"
          >
            <MessageCircle size={18} /> Avis clients
          </button>
        </div>

        {activeTab === 'reviews' ? (
          <AdminReviewsPanel />
        ) : (
        <>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Total réservations</span>
                <Plane className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-gray-900" data-testid="stat-total">{stats.total}</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Ce mois</span>
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.this_month}</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">En attente</span>
                <Clock className="text-yellow-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.by_status.pending}</div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Confirmées</span>
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.by_status.confirmed}</div>
            </div>
          </div>
        )}

        {/* Visits Stats */}
        {stats?.visits && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="text-blue-600" size={20} />
                Trafic du site
              </h3>
              <span className="text-xs text-gray-400">7 derniers jours</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-blue-600 font-medium">Aujourd'hui</span>
                  <Eye className="text-blue-500" size={16} />
                </div>
                <div className="text-2xl font-bold text-blue-900" data-testid="visits-today">{stats.visits.today}</div>
              </div>
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-emerald-600 font-medium">Cette semaine</span>
                  <TrendingUp className="text-emerald-500" size={16} />
                </div>
                <div className="text-2xl font-bold text-emerald-900">{stats.visits.this_week}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-purple-600 font-medium">Ce mois</span>
                  <Calendar className="text-purple-500" size={16} />
                </div>
                <div className="text-2xl font-bold text-purple-900">{stats.visits.this_month}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 font-medium">Total</span>
                  <Eye className="text-gray-500" size={16} />
                </div>
                <div className="text-2xl font-bold text-gray-900" data-testid="visits-total">{stats.visits.total}</div>
              </div>
            </div>

            {/* Mini Bar Chart */}
            {stats.visits.daily && stats.visits.daily.length > 0 && (
              <div>
                <div className="flex items-end gap-2 h-32">
                  {stats.visits.daily.map((d, idx) => {
                    const max = Math.max(...stats.visits.daily.map(x => x.count), 1);
                    const heightPct = (d.count / max) * 100;
                    const date = new Date(d.date);
                    const dayLabel = date.toLocaleDateString('fr-FR', { weekday: 'short' }).slice(0, 3);
                    const dayNum = date.getDate();
                    const isToday = idx === stats.visits.daily.length - 1;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                        <div className="text-xs font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">
                          {d.count}
                        </div>
                        <div
                          className={`w-full rounded-t-lg transition-all duration-300 ${
                            isToday ? 'bg-blue-600' : 'bg-blue-200 group-hover:bg-blue-400'
                          }`}
                          style={{ height: `${Math.max(heightPct, 3)}%` }}
                          title={`${d.date}: ${d.count} visite(s)`}
                        />
                        <div className="text-xs text-gray-500 mt-1 text-center">
                          <div className="capitalize">{dayLabel}</div>
                          <div className="text-gray-400">{dayNum}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4 italic">
              Compteur interne · Pour des statistiques détaillées (sources, pays, etc.), consultez Google Analytics
            </p>
          </div>
        )}

        {/* Top destinations */}
        {stats?.top_destinations?.length > 0 && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-8">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="text-blue-600" size={20} />
              Destinations populaires
            </h3>
            <div className="flex flex-wrap gap-2">
              {stats.top_destinations.map((d, i) => (
                <div key={i} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">
                  {d.city} <span className="text-blue-500 font-bold ml-1">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters + Exports */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-6 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, ville..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-11 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer"
              data-testid="status-filter"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="contacted">Contacté</option>
              <option value="confirmed">Confirmé</option>
              <option value="cancelled">Annulé</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
          </div>

          {/* Export buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => exportData('excel')}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-sm"
              data-testid="export-excel-btn"
              title="Exporter en Excel"
            >
              <FileSpreadsheet size={18} />
              <span className="hidden sm:inline">Excel</span>
            </button>
            <button
              onClick={() => exportData('pdf')}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors shadow-sm"
              data-testid="export-pdf-btn"
              title="Exporter en PDF"
            >
              <FileText size={18} />
              <span className="hidden sm:inline">PDF</span>
            </button>
          </div>
        </div>

        {/* Bookings List */}
        <div className="space-y-3">
          {loading && bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Chargement...</div>
          ) : bookings.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl text-center text-gray-500 border border-gray-100">
              <Plane className="mx-auto mb-3 text-gray-300" size={48} />
              <p>Aucune réservation pour le moment</p>
            </div>
          ) : (
            bookings.map((b) => {
              const StatusIcon = statusConfig[b.status]?.icon || Clock;
              const isExpanded = expandedBooking === b.id;
              return (
                <div
                  key={b.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`booking-card-${b.id}`}
                >
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => setExpandedBooking(isExpanded ? null : b.id)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900">{b.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusConfig[b.status]?.color}`}>
                            <StatusIcon size={12} />
                            {statusConfig[b.status]?.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                          <span className="flex items-center gap-1"><Mail size={14} />{b.email}</span>
                          <span className="flex items-center gap-1"><Phone size={14} />{b.phone}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-700">
                          <span className="font-medium">{tripTypeLabels[b.tripType] || b.tripType}</span>
                          {b.tripType !== 'multiCity' && (
                            <span> · {b.fromCity || '?'} → {b.toCity || '?'}</span>
                          )}
                          {b.tripType === 'multiCity' && b.flights && (
                            <span> · {b.flights.length} vols</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 hidden md:block">{formatDate(b.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">Voyage</h4>
                          {b.tripType === 'multiCity' && b.flights ? (
                            <ul className="space-y-1">
                              {b.flights.map((f, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <Plane size={14} className="text-blue-600" />
                                  <span>{f.from} → {f.to} ({f.date})</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2"><MapPin size={14} className="text-blue-500" /> De: {b.fromCity}</div>
                              <div className="flex items-center gap-2"><MapPin size={14} className="text-red-500" /> Vers: {b.toCity}</div>
                              <div className="flex items-center gap-2"><Calendar size={14} /> Départ: {b.departureDate || '-'}</div>
                              {b.returnDate && <div className="flex items-center gap-2"><Calendar size={14} /> Retour: {b.returnDate}</div>}
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">Passagers</h4>
                          <div className="flex items-center gap-3">
                            <Users size={16} className="text-blue-600" />
                            <span>
                              {b.passengers.adults} adulte(s), {b.passengers.children} enfant(s), {b.passengers.infants} bébé(s)
                            </span>
                          </div>
                          {b.message && (
                            <>
                              <h4 className="font-semibold text-gray-700 mt-3 mb-1">Message</h4>
                              <p className="text-gray-600 italic bg-white p-2 rounded">{b.message}</p>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                        {Object.keys(statusConfig).map((s) => (
                          <button
                            key={s}
                            onClick={(e) => { e.stopPropagation(); updateStatus(b.id, s); }}
                            disabled={b.status === s}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              b.status === s
                                ? statusConfig[s].color + ' ring-2 ring-offset-1 ring-blue-400'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`status-${s}-${b.id}`}
                          >
                            {statusConfig[s].label}
                          </button>
                        ))}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteBooking(b.id); }}
                          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center gap-1"
                          data-testid={`delete-${b.id}`}
                        >
                          <Trash2 size={12} />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalBookings > 0 && (
          <div className="mt-6 bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span>
                <span className="font-semibold text-gray-900">{(page - 1) * pageSize + 1}</span>
                {' - '}
                <span className="font-semibold text-gray-900">{Math.min(page * pageSize, totalBookings)}</span>
                {' sur '}
                <span className="font-semibold text-gray-900">{totalBookings}</span>
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                data-testid="page-size-select"
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-first"
              >
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-prev"
              >
                <ChevronLeft size={18} />
              </button>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || (n >= page - 1 && n <= page + 1))
                .map((n, idx, arr) => (
                  <React.Fragment key={n}>
                    {idx > 0 && arr[idx - 1] !== n - 1 && (
                      <span className="px-2 text-gray-400">…</span>
                    )}
                    <button
                      onClick={() => setPage(n)}
                      className={`min-w-[36px] px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                        page === n
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                      data-testid={`pagination-page-${n}`}
                    >
                      {n}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-next"
              >
                <ChevronRight size={18} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-last"
              >
                »
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
